import {ChangeEvent, Fragment, useRef, useState} from "react";
import {formEl} from "../constants";
import {Option, QuestionTypes, SubQuestion} from "../../../types/Question";
import SpeakInput from "./SpeakInput";
import Nestable from "react-nestable";
import Paper from "../../ui/customs/Paper";
import Select from "../../form/Select";
import {Copy, GripVertical, Trash2} from "lucide-react";
import {DateInput, NumberInput, RadioInput, TextArea, TextFieldInput} from "./index";
import DragAndDropInput from "./DragAndDropInput";
import CheckboxInput from "./CheckboxInput";
import MyEditor5 from "../../form/input/MyEditor5";

type ListenProps = {
    item: {
        id: string;
        uid: string;
        title: string;
        value: string | null;
        required: boolean;
        type: string;
        parentId: string;
        childs: SubQuestion[];
        file?: {
            publicPath: string;
            name: string;
        };
    };
    handleValue: (id: string, newValue: string, type: QuestionTypes) => void;
    handleAnswerValue: (id: string, event: React.ChangeEvent<HTMLInputElement>, type: QuestionTypes) => void;
    deleteEl: (uuid: {uid: string, id?: string}) => void;
    handleOnChangeSort: (parentId: string, items: any) => void;
    handleRequired: (id: string) => void;
    handleElType: (id: string, value: QuestionTypes) => void;
    handleElTypeChildQuestion: (id: string, value: QuestionTypes) => void;
    duplicateElement: (id: string, type: QuestionTypes) => void;
    addSubQuestion: (parentId: string) => void;
    functions: {
        handleAnswerValueChildQuestion: (id: string, newValue: string, type: QuestionTypes) => void;
        handleDate: (id: string, dateVal: Date | null) => void;
        handleTime: (id: string, dateVal: Date | null) => void;
        addOptionChildQuestion: (id: string, newOption: Option) => void;
        handleOptionValuesChildQuestion: (elId: string, answer: {optionId: string, uid: string, optionVal: string, isCorrect: number}, type: QuestionTypes) => void;
        deleteOptionChildQuestion: (elId: string, uid: string, optionId: string) => void;
        deleteElChildQuestion: (uuid: {uid: string, id?: string}) => void;
        duplicateElementChildQuestion: (id: string, type: QuestionTypes) => void;
        handleValueChildQuestion: (id: string, newValue: string, type: QuestionTypes) => void;
        handleUpdateChildAnswersOrder: (itemId: string, newAnswers: Option[]) => void;
        handleUpdateChildViewOrder: (itemId: string, displayAnswers: Option[]) => void;
    }
    isSubQuestion?: boolean;
};

const Listen: React.FC<ListenProps> = ({
                                                         item,
                                                         handleValue,
                                                         handleAnswerValue,
                                                         handleOnChangeSort,
                                                         deleteEl,
                                                         handleRequired,
                                                         handleElType,
                                           handleElTypeChildQuestion,
                                                         duplicateElement,
                                                         addSubQuestion,
                                                         functions,
                                                         isSubQuestion = false,
                                                     }) => {
    const {
        handleAnswerValueChildQuestion,
        handleDate,
        handleTime,
        addOptionChildQuestion,
        handleOptionValuesChildQuestion,
        deleteOptionChildQuestion,
        deleteElChildQuestion,
        duplicateElementChildQuestion,
        handleValueChildQuestion,
        handleUpdateChildAnswersOrder,
        handleUpdateChildViewOrder,
    } = functions;
    const [uploadedFile, setUploadedFile] = useState<{ publicPath: string; name: string } | null>(item.file || null);

    const handleOnChangeSortChild = (items: any) => {
        handleOnChangeSort(item.id, items);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const publicPath = URL.createObjectURL(file);
            setUploadedFile({ publicPath, name: file.name });
            handleAnswerValue(item.id, e, QuestionTypes.QUESTION_TYPE_LISTEN);
        }
    };

    const fileInputRef = useRef<HTMLInputElement>(null);

    const renderElements = ({ item, index, handler, depth, collapseIcon }: any) => {
        return (
            <div className="bg-gray-50 p-4 rounded shadow" key={item.id}>
                <div className="flex items-center gap-2 mb-2">
                    {handler}
                    <span className="text-gray-500 text-xs">{index + 1}</span>
                </div>

                {(() => {
                    switch (item.type) {
                        case QuestionTypes.QUESTION_TYPE_SHORT_ANSWER:
                            return <TextFieldInput item={item} handleValue={handleValueChildQuestion} handleAnswerValue={handleAnswerValueChildQuestion} deleteEl={deleteElChildQuestion}
                                                   handleRequired={handleRequired} handleElType={handleElTypeChildQuestion} duplicateElement={duplicateElementChildQuestion} isSubQuestion={true} />;
                        case QuestionTypes.QUESTION_TYPE_LONG_ANSWER:
                            return <TextArea item={item} handleValue={handleValueChildQuestion} handleAnswerValue={handleAnswerValueChildQuestion} deleteEl={deleteElChildQuestion}
                                             handleRequired={handleRequired} handleElType={handleElTypeChildQuestion} duplicateElement={duplicateElementChildQuestion} isSubQuestion={true} />;
                        case "number":
                            return <NumberInput item={item} handleValue={handleValueChildQuestion} deleteEl={deleteElChildQuestion} handleRequired={handleRequired}
                                                handleElType={handleElTypeChildQuestion} duplicateElement={duplicateElementChildQuestion} isSubQuestion={true} />;
                        case QuestionTypes.QUESTION_TYPE_SELECT_OPTION:
                            return <RadioInput currentType={QuestionTypes.QUESTION_TYPE_SELECT_OPTION} item={item} handleValue={handleValueChildQuestion}
                                               deleteEl={deleteElChildQuestion} handleRequired={handleRequired} handleElType={handleElTypeChildQuestion} addOption={addOptionChildQuestion}
                                               handleOptionValues={handleOptionValuesChildQuestion} deleteOption={deleteOptionChildQuestion} duplicateElement={duplicateElementChildQuestion} isSubQuestion={true} />;
                        case QuestionTypes.QUESTION_TYPE_SINGLE:
                            return <RadioInput currentType={QuestionTypes.QUESTION_TYPE_SINGLE} item={item} handleValue={handleValueChildQuestion} deleteEl={deleteElChildQuestion}
                                               handleRequired={handleRequired} handleElType={handleElTypeChildQuestion} addOption={addOptionChildQuestion} handleOptionValues={handleOptionValuesChildQuestion}
                                               deleteOption={deleteOptionChildQuestion} duplicateElement={duplicateElementChildQuestion} isSubQuestion={true} />;
                        case QuestionTypes.QUESTION_TYPE_SPEAK:
                            return <SpeakInput item={item} handleValue={handleValueChildQuestion} deleteEl={deleteElChildQuestion} handleRequired={handleRequired}
                                               handleElType={handleElTypeChildQuestion} duplicateElement={duplicateElementChildQuestion} isSubQuestion={true} />;
                        case QuestionTypes.QUESTION_TYPE_DRAG_AND_DROP:
                            return <DragAndDropInput updateViewOrder={handleUpdateChildViewOrder} updateAnswersOrder={handleUpdateChildAnswersOrder} currentType={QuestionTypes.QUESTION_TYPE_DRAG_AND_DROP} item={item} handleValue={handleValueChildQuestion}
                                                     deleteEl={deleteElChildQuestion} handleRequired={handleRequired} handleElType={handleElTypeChildQuestion} addOption={addOptionChildQuestion}
                                                     handleOptionValues={handleOptionValuesChildQuestion} deleteOption={deleteOptionChildQuestion} duplicateElement={duplicateElementChildQuestion} isSubQuestion={true} />;
                        case QuestionTypes.QUESTION_TYPE_MULTI:
                            return <CheckboxInput currentType={QuestionTypes.QUESTION_TYPE_MULTI} item={item} handleValue={handleValueChildQuestion}
                                                  deleteEl={deleteElChildQuestion} handleRequired={handleRequired} handleElType={handleElTypeChildQuestion} addOption={addOptionChildQuestion}
                                                  handleOptionValues={handleOptionValuesChildQuestion} deleteOption={deleteOptionChildQuestion} duplicateElement={duplicateElementChildQuestion} isSubQuestion={true} />;
                        case "date":
                        // return <DateInput item={item} handleValue={handleValueChildQuestion} deleteEl={deleteElChildQuestion} handleRequired={handleRequired} handleElType={handleElTypeChildQuestion} duplicateElement={duplicateElementChildQuestion} />;
                        case "time":
                            return <div>TODO: Thêm TimeInput ở đây</div>;
                        default:
                            return <Fragment />;
                    }
                })()}
            </div>
        );
    };

    return (
        <Fragment>
            <Paper className="mb-6 mt-6">
                <div className="flex justify-between items-center mb-4">
                    <GripVertical className="text-gray-400 cursor-move"/>
                    <div className="w-1/4">
                        {isSubQuestion ? (
                            <Select
                                defaultValue={String(item.type)}
                                onChange={(val) => handleElTypeChildQuestion(item.id, Number(val))}
                                options={formEl.filter((el) => !el.hasChildren).map((el) => ({
                                    label: el.label,
                                    value: String(el.value),
                                }))}
                            />
                        ) : (
                            <Select
                                defaultValue={String(item.type)}
                                onChange={(val) => handleElType(item.id, Number(val))}
                                options={formEl.map((el) => ({
                                    label: el.label,
                                    value: String(el.value),
                                }))}
                            />
                        )}
                    </div>
                </div>

                <div className="bg-white border-b p-4 grid grid-cols-1 md:grid-cols-12 gap-4">
                    <div className="md:col-span-12 space-y-4">
                        <MyEditor5
                            content={item.title}
                            onBlur={(value) => handleValue(item.id, value, QuestionTypes.QUESTION_TYPE_GROUP)}
                        />
                        <input
                            ref={fileInputRef}
                            type="file"
                            placeholder=""
                            accept="audio/*"
                            onChange={handleFileChange}
                            className="w-full border border-gray-300 rounded-lg p-3 text-sm shadow-sm focus:ring focus:ring-indigo-200 focus:outline-none"
                        />
                        {uploadedFile && (
                            <div className="mt-2">
                                <audio controls src={uploadedFile.publicPath} className="w-full">
                                    Trình duyệt của bạn không hỗ trợ audio.
                                </audio>
                                <button
                                    type="button"
                                    className="ml-2 text-red-500 hover:underline text-xs"
                                    onClick={() => {
                                        setUploadedFile(null);
                                        if (fileInputRef.current) {
                                            fileInputRef.current.value = "";
                                        }
                                    }}
                                >
                                    Xoá file
                                </button>
                                <a
                                    href={uploadedFile.publicPath}
                                    download={uploadedFile.name}
                                    className="inline-block mt-1 text-indigo-600 hover:underline text-xs"
                                >
                                    Tải file hiện tại
                                </a>
                            </div>
                        )}
                    </div>
                </div>

                {item?.childs?.length > 0 && (
                    <div className="bg-white p-4 space-y-4">
                        <Nestable
                            items={item.childs}
                            renderItem={renderElements}
                            maxDepth={1}
                            onChange={handleOnChangeSortChild}
                        />
                    </div>
                )}

                <button
                    className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm mt-4 px-4 py-2 rounded transition"
                    onClick={() => addSubQuestion(item.id)}
                >
                    + Thêm câu hỏi con
                </button>

                <div className="mt-4 flex items-center space-x-4 border-t pt-3">
                    <button
                        className="text-gray-500 hover:text-gray-800"
                        onClick={() => deleteEl({uid: item.uid, id: item.id})}
                        title="Xoá phần tử"
                    >
                        <Trash2 size={18}/>
                    </button>
                    <button
                        className="text-gray-500 hover:text-gray-800"
                        onClick={() => duplicateElement(item.id, QuestionTypes.QUESTION_TYPE_SINGLE)}
                        title="Nhân bản"
                    >
                        <Copy size={18}/>
                    </button>

                    <label className="inline-flex items-center ml-auto text-sm text-gray-600">
                        <input
                            type="checkbox"
                            checked={item.required}
                            onChange={() => handleRequired(item.id)}
                            className="mr-2"
                        />
                        Bắt buộc
                    </label>
                </div>
            </Paper>
        </Fragment>
    );
};

export default Listen;
